import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from '~/components/ui/inputs/MultiSelect';
import type { LengthFilterOption } from './lengthFilterOptions';

type LengthPickerProps = {
  readonly options: LengthFilterOption[];
  readonly selectedIds: string[];
  readonly onChange: (ids: string[]) => void;
  readonly selectedChipsId: string;
};

export function LengthPicker({
  options,
  selectedIds,
  onChange,
  selectedChipsId,
}: LengthPickerProps) {
  const { t } = useTranslation('order-builder');
  return (
    <div className="w-40">
      <MultiSelect
        searchable
        alwaysShowPlaceholder
        testId="length-multiselect"
        name="lengths"
        ariaLabel={t('order_builder.filters.filter_by_length')}
        ariaDescribedBy={selectedChipsId}
        placeholder={
          selectedIds.length
            ? t('order_builder.filters.filter_lengths')
            : t('order_builder.filters.all_lengths')
        }
        options={options}
        value={selectedIds}
        onChange={(_name, values) => onChange(values)}
        size="sm"
      />
    </div>
  );
}

type SelectedLengthChipsProps = {
  readonly options: LengthFilterOption[];
  readonly selectedIds: string[];
  readonly onChange: (ids: string[]) => void;
  readonly id: string;
};

export function SelectedLengthChips({
  options,
  selectedIds,
  onChange,
  id,
}: SelectedLengthChipsProps) {
  const { t } = useTranslation('order-builder');
  const selected = options.filter((option) =>
    selectedIds.includes(option.value)
  );
  return (
    <div
      id={id}
      className="flex flex-wrap items-center gap-1.5"
      data-testid="selected-lengths"
    >
      {selected.map((option) => (
        <span
          key={option.value}
          className="inline-flex items-center gap-0.5 rounded-full bg-neut-100 py-1 pl-3 pr-1 text-xs font-medium text-neut-700"
        >
          {option.label}
          <button
            type="button"
            data-testid={`remove-length-${option.value}`}
            aria-label={t('order_builder.filters.remove_length', {
              length: option.label,
            })}
            className="flex h-5 w-5 items-center justify-center rounded-full text-neut-500 transition-colors hover:bg-neut-200 hover:text-neut-900"
            onClick={() =>
              onChange(selectedIds.filter((value) => value !== option.value))
            }
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </span>
      ))}
    </div>
  );
}
